'use client';

import { useSiteCollectionEditor } from "@/app/features/sites/collection/contexts/SiteCollectionEditorContext";
import { notNull } from "@/app/lib/common/not-null";
import SnappySections from "@/app/features/shared/snap-section/SnapSectionsContainer";
import { Divider } from "@mui/material";
import Section from "@/app/features/shared/snap-section/Section";
import { DetailSectionEditable } from "@/app/features/shared/detail-section";
import { DetailEditable } from "@/app/features/shared/types/types";
import ShowroomEditable from "@/app/features/shared/showroom/ui/editable/Showroom.editable";
import RelatedInfosView from "@/app/features/shared/related-infos/ui/RelatedInfos.public";
import { RelatedInfo } from "@/app/lib/generated/graphql-types";
import type { SiteCollectionEditable } from "@/app/features/sites/collection/types/types";

type CollectionShowroom = NonNullable<SiteCollectionEditable["showroom"]>;

export default function CollectionEditable() {

    const { editable, updateEditable } = useSiteCollectionEditor();

    function updateDetailByIndex(indexToUpdate: number, partialDetail: Partial<DetailEditable>) {
        updateEditable({
            details: editable?.details.filter(notNull).map((detail, index) => {
                return index === indexToUpdate
                    ? { ...detail, ...partialDetail }
                    : detail

            })
        })
    }

    function updateShowroom(partialShowroom: Partial<CollectionShowroom>) {
        if (!editable?.showroom) return;

        updateEditable({
            showroom: {
                ...editable.showroom,
                ...partialShowroom,
            },
        });
    }

    return (
        <SnappySections>
            {editable?.details.filter(notNull).map((detail, index) => (
                <div key={index} className="w-full">
                    <Divider sx={{ m: 4 }} />
                    <Section>
                        <DetailSectionEditable
                            data={detail}
                            alignment={index % 2 === 0 ? "row" : "row-reverse"}
                            onTitleChange={(title) => updateDetailByIndex(index, { title })}
                            onSubtitleChange={(subtitle) => updateDetailByIndex(index, { subtitle })}
                            onDescriptionChange={(description) => updateDetailByIndex(index, { description })}
                            onImageChange={(image) => updateDetailByIndex(index, { image })}
                        />
                    </Section>
                </div>
            ))}
            {editable?.showroom && (
                <Section contentHeight="fit-content">
                    <ShowroomEditable
                        data={editable.showroom}
                        onTitleChange={(title) => updateShowroom({ title })}
                        onSubtitleChange={(subtitle) => updateShowroom({ subtitle })}
                        onDescriptionChange={(description) => updateShowroom({ description })}
                        onImagesChange={(images) => updateShowroom({ images })}
                    />
                </Section>
            )}

            {editable?.relatedInfos && (
                <RelatedInfosView relatedInfos={editable.relatedInfos.filter(notNull) as RelatedInfo[]} />
            )}
        </SnappySections>
    )
}
